/**
 * GraphViewer.tsx - Universal graph visualization component with layout switching
 * 
 * CHANGE HISTORY:
 * - 251031_2050 [pending] danielbeer: Initial creation with 6 layout types
 * 
 * Main component that renders different graph layouts based on user selection.
 * Provides layout switcher UI and delegates to specific layout components.
 */

import React, { useState } from 'react';
import type { GraphProps, GraphLayout, GraphLegendItem } from './types';
import { ForceDirectedGraph } from './ForceDirectedGraph';
import { HierarchicalTreeGraph } from './HierarchicalTreeGraph';
import { RadialGraph } from './RadialGraph';
import { SankeyGraph } from './SankeyGraph';
import { MatrixGraph } from './MatrixGraph';
import { LayeredDAGGraph } from './LayeredDAGGraph';
import './GraphViewer.css';

export interface GraphViewerProps<T = any> extends Omit<GraphProps<T>, 'layout'> {
  defaultLayout?: GraphLayout;
  showLayoutSwitcher?: boolean;
  availableLayouts?: GraphLayout[];
}

/**
 * GraphViewer Component
 * 
 * Universal graph visualization with multiple layout options:
 * - Force-directed: Physics-based node positioning
 * - Tree: Hierarchical top-down or left-right
 * - Radial: Circular layout from center
 * - Sankey: Flow-based visualization
 * - Matrix: Grid-based relationship view
 * - DAG: Layered directed acyclic graph
 */
export const GraphViewer = <T = any,>({
  data,
  defaultLayout = 'force',
  showLayoutSwitcher = true,
  availableLayouts = ['force', 'tree', 'radial', 'sankey', 'matrix', 'dag'],
  width = 1200,
  height = 800,
  ...props
}: GraphViewerProps<T>) => {
  const [currentLayout, setCurrentLayout] = useState<GraphLayout>(defaultLayout);

  // Render the appropriate graph component based on layout
  const renderGraph = () => {
    const commonProps = {
      data,
      width,
      height,
      ...props,
    };

    switch (currentLayout) {
      case 'force':
        return <ForceDirectedGraph {...commonProps} />;
      case 'tree':
        return <HierarchicalTreeGraph {...commonProps} />;
      case 'radial':
        return <RadialGraph {...commonProps} />;
      case 'sankey':
        return <SankeyGraph {...commonProps} />;
      case 'matrix':
        return <MatrixGraph {...commonProps} />;
      case 'dag':
        return <LayeredDAGGraph {...commonProps} />;
      default:
        return <ForceDirectedGraph {...commonProps} />;
    }
  };

  return (
    <div className="graph-viewer">
      {showLayoutSwitcher && (
        <div className="graph-layout-switcher">
          <div className="layout-switcher-label">Layout:</div>
          <div className="layout-switcher-buttons">
            {availableLayouts.map((layout) => (
              <button
                key={layout}
                className={`layout-btn ${currentLayout === layout ? 'active' : ''}`}
                onClick={() => setCurrentLayout(layout)}
                title={getLayoutDescription(layout)}
              >
                <span className="layout-icon">{getLayoutIcon(layout)}</span>
                <span className="layout-name">{getLayoutName(layout)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      
      <div className="graph-container">
        {renderGraph()}
      </div>

      {/* Layout description */}
      <div className="graph-layout-info">
        <strong>{getLayoutName(currentLayout)}</strong>: {getLayoutDescription(currentLayout)}
      </div>
    </div>
  );
};

/**
 * Get human-readable layout name
 */
function getLayoutName(layout: GraphLayout): string {
  const names: Record<GraphLayout, string> = {
    force: 'Force-Directed',
    tree: 'Hierarchical Tree',
    radial: 'Radial',
    sankey: 'Sankey Flow',
    matrix: 'Matrix',
    dag: 'Layered DAG',
  };
  return names[layout];
}

/**
 * Get layout icon
 */
function getLayoutIcon(layout: GraphLayout): string {
  const icons: Record<GraphLayout, string> = {
    force: '🌐',
    tree: '🌳',
    radial: '⭕',
    sankey: '➡️',
    matrix: '⬜',
    dag: '📊',
  };
  return icons[layout];
}

/**
 * Get layout description
 */
function getLayoutDescription(layout: GraphLayout): string {
  const descriptions: Record<GraphLayout, string> = {
    force: 'Physics-based layout with nodes repelling each other and edges pulling them together',
    tree: 'Hierarchical structure showing parent-child relationships in a tree format',
    radial: 'Circular layout with nodes arranged in concentric circles from a center point',
    sankey: 'Flow diagram showing the magnitude of connections between nodes',
    matrix: 'Grid-based view showing all relationships in a matrix format',
    dag: 'Layered layout organizing nodes by dependency levels',
  };
  return descriptions[layout];
}

export default GraphViewer;
